import ErrorMessage from "components/ErrorMessage";
import ODateTimePicker from "components/shared/datePicker/ODateTimePicker";
import AuthContext from "context/AuthContext";
import { isEmpty } from "lodash";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { TiTrash } from "react-icons/ti";
import { useNavigate } from "react-router-dom";
import { apiPost } from "utils/apiFetch";
import apiPath from "utils/apiPath";
import Input from "../../../components/Input";
import QuestionDialog from "../../../components/QuestionDialog";
import { Button } from "@mui/material";

function AddQuestionBankPage() {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const [title, setTitle] = useState("");
  const [titleAr, setTitleAr] = useState("");
  const [questions, setQuestions] = useState([]);
  const [description, setDescription] = useState("");
  const [descriptionAr, setDescriptionAr] = useState("");

  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  function questionAddedHandler(question) {
    setQuestions([...questions, question]);
    setOpen(false);
  }

  function deleteQuestionHandler(index) {
    setQuestions(questions.filter((q, i) => i !== index));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!isValid()) return;

    try {
      const response = await apiPost(apiPath.quizQuestionBank, {
        title,
        titleAr,
        description,
        descriptionAr,
      });
      if (!response?.data?.success) return;

      const questionBankId = response?.data?.results?._id;

      for (const question of questions) {
        const { data } = await apiPost(apiPath.quizQuestions, {
          questionBankId,
          title: question.title,
          titleAr: question.titleAr,
          type: question.type,
          points: question.points,
          expectedAnswer: question.expectedAnswer,
          options: question.options.map((option) => ({
            text: option.title,
            textAr: option.titleAr,
            isCorrect: option.isCorrect,
          })),
          matchingPairs: question.matchingPairs.map((pair) => ({
            left: {
              text: pair.left.text,
              textAr: pair.left.textAr,
            },
            right: {
              text: pair.right.text,
              textAr: pair.right.textAr,
            },
          })),
        });

        if (!data?.success) return;
        navigate("/test-manager/tests");
      }
    } catch (error) {
      console.log(error);
    }
  }

  function isValid() {
    return !(
      isEmpty(title) ||
      isEmpty(titleAr) ||
      isEmpty(description) ||
      isEmpty(descriptionAr)
    );
  }

  return (
    <div className="bg-white px-3 py-4 ">
      <h1 className="text-2xl font-bold">{t("O_ADD_QUESTION_BANK")}</h1>
      <hr className="my-4" />
      <form className="flex flex-col gap-4 " onSubmit={handleSubmit}>
        <div className="flex gap-4">
          <Input
            label={t("O_TITLE")}
            placeholder={t("O_TITLE")}
            required
            name="title"
            onChange={(e) => setTitle(e.target.value)}
          />
          <Input
            label={t("O_TITLE") + t("O_ARABIC")}
            placeholder={t("O_TITLE") + t("O_ARABIC")}
            required
            name="titleAr"
            onChange={(e) => setTitleAr(e.target.value)}
          />
        </div>
        <div className="flex gap-4">
          <Input
            label={t("O_DESCRIPTION")}
            placeholder={t("O_DESCRIPTION")}
            required
            name="description"
            onChange={(e) => setDescription(e.target.value)}
          />
          <Input
            label={t("O_DESCRIPTION") + t("O_ARABIC")}
            placeholder={t("O_DESCRIPTION") + t("O_ARABIC")}
            required
            name="descriptionAr"
            onChange={(e) => setDescriptionAr(e.target.value)}
          />
        </div>

        <hr className="my-4" />

        <div className="flex flex-col gap-4">
          <QuestionsTable
            questions={questions}
            deleteQuestionHandler={deleteQuestionHandler}
          />
        </div>
        <Button
          type="button"
          variant="contained"
          color="primary"
          className="self-center"
          onClick={() => setOpen(true)}
        >
          {t("O_ADD_QUESTION")}
        </Button>
        <QuestionDialog
          questionAddedHandler={questionAddedHandler}
          closeHandler={() => setOpen(false)}
          open={open}
        />
        <hr className="my-4" />
        <button
          type="submit"
          disabled={!isValid()}
          className={`bg-blue-500 text-white px-4 py-2 rounded-md  text-nowrap self-center ${
            !isValid() ? "bg-gray-500" : ""
          }`}
        >
          {t("O_ADD_QUESTION_BANK")}
        </button>
      </form>
    </div>
  );
}

export default AddQuestionBankPage;

function QuestionsTable({ questions, deleteQuestionHandler }) {
  const { t } = useTranslation();
  if (questions?.length === 0 || isEmpty(questions)) return null;
  return (
    <table className="w-full text-sm text-left text-black  ">
      <thead className="text-sm   border border-[#E1E6EE]  bg-gray-700  text-white">
        <tr className="py-3 px-6">
          <th className="py-3 px-6">{t("O_TITLE")}</th>
          <th className="py-3 px-6">{t("O_TITLE") + t("O_ARABIC")}</th>
          <th className="py-3 px-6">{t("O_DESCRIPTION")}</th>
          <th className="py-3 px-6">{t("O_DESCRIPTION") + t("O_ARABIC")}</th>
          <th className="py-3 px-6">{t("O_TYPE")}</th>
          <th className="py-3 px-6">{t("O_POINTS")}</th>
          <th className="py-3 px-6">{t("O_EXPECTED_ANSWER")}</th>
          <th className="py-3 px-6">{t("O_ACTION")}</th>
        </tr>
      </thead>
      <tbody>
        {questions.map((question, index) => (
          <tr className="py-3 px-6 border-b border-[#E1E6EE]" key={index}>
            <td className="py-3 px-6">{question.title || "-"}</td>
            <td className="py-3 px-6">{question.titleAr || "-"}</td>
            <td className="py-3 px-6">{question.description || "-"}</td>
            <td className="py-3 px-6">{question.descriptionAr || "-"}</td>
            <td className="py-3 px-6">{question.type || "-"}</td>
            <td className="py-3 px-6">{question.points || "-"}</td>
            <td className="py-3 px-6">{question.expectedAnswer || "-"}</td>
            <td className="py-3 px-6 flex gap-2">
              <button
                type="button"
                className="bg-red-500  text-white px-4 py-2 rounded-md"
                onClick={() => deleteQuestionHandler(index)}
              >
                <TiTrash />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
